import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../utils/AxiosInstance";
import { useAuth } from "../context/AuthContext";
import SearchContainer from "../components/SearchContainer";

const MAX_KEYWORDS = 5;
const RESULTS_PER_PAGE_OPTIONS = [10, 25, 50, 100];

const Scout = () => {
  const { accessToken } = useAuth();

  const [keywords, setKeywords] = useState([]);
  const [newKeyword, setNewKeyword] = useState("");
  const [error, setError] = useState(null);

  const [results, setResults] = useState({});
  const [loading, setLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [enableFuzzing, setEnableFuzzing] = useState(false);

  const [ransomwareResults, setRansomwareResults] = useState([]);

  // Pagination and display controls
  const [currentPage, setCurrentPage] = useState(1);
  const [resultsPerPage, setResultsPerPage] = useState(10);

  // Sorting state: { column: string, direction: "asc" | "desc" }
  const [sortConfig, setSortConfig] = useState({ column: "keyword", direction: "asc" });

  // Filtering inputs
  const [filterKeyword, setFilterKeyword] = useState("");
  const [filterSource, setFilterSource] = useState("");

  // Load saved keywords
  useEffect(() => {
    const saved = localStorage.getItem("iotScoutKeywords");
    if (saved) {
      try {
        setKeywords(JSON.parse(saved));
      } catch {
        localStorage.removeItem("iotScoutKeywords");
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("iotScoutKeywords", JSON.stringify(keywords));
  }, [keywords]);

    // Utility to convert array of objects to CSV string
  const convertToCSV = (arr) => {
    if (!arr.length) return "";

    const headers = Object.keys(arr[0]);
    const escapeCSV = (text) => {
      if (typeof text !== "string") return text;
      if (text.includes(",") || text.includes('"') || text.includes("\n")) {
        return `"${text.replace(/"/g, '""')}"`;
      }
      return text;
    };

    const csvRows = [
      headers.join(","), // header row
      ...arr.map((row) => headers.map((field) => escapeCSV(row[field])).join(",")),
    ];

    return csvRows.join("\n");
  };

  const handleExport  = () => {
    // Export currently paginated results, or
    // change to sortedResults to export all filtered+sorted data
    const dataToExport = paginatedResults;

    if (!dataToExport.length) {
      alert("No data available to export");
      return;
    }

    const csv = convertToCSV(dataToExport);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "scout-results.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };
  const addKeyword = () => {
    const trimmed = newKeyword.trim().toLowerCase();
    if (!trimmed) return setError("Keyword cannot be empty");
    if (keywords.includes(trimmed)) return setError("Keyword already added");
    if (keywords.length >= MAX_KEYWORDS)
      return setError(`Max ${MAX_KEYWORDS} keywords allowed`);

    setKeywords([...keywords, trimmed]);
    setNewKeyword("");
    setError(null);
  };

  const removeKeyword = (kw) => {
    setKeywords(keywords.filter((k) => k !== kw));
  };

  const searchKeywords = async () => {
    if (keywords.length === 0) {
      setSearchError("Add at least one keyword to search");
      return;
    }
    setSearchError(null);
    setLoading(true);
    setResults({});
    setCurrentPage(1);
    try {
    const response = await axiosInstance.post(
        "discover/scout/",
        { keywords, enable_fuzzing: enableFuzzing },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setResults(response.data.results || {});
      setRansomwareResults(response.data.ransomware_breaches || []);  // <-- important
    } catch (err) {
      setSearchError("Error during search. Please try again.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Flatten and transform results for table usage
  const flatResults = useMemo(() => {
    const arr = [];
    for (const [keyword, sources] of Object.entries(results)) {
      for (const [source, indicators] of Object.entries(sources)) {
        if (Array.isArray(indicators)) {
          indicators.forEach((item) => {
            const indicatorValue = item.match ?? item.indicator ?? "N/A";
            arr.push({
              keyword: item.keyword || keyword,
              source: item.source || source,
              indicator: indicatorValue,
              ioc_created_at: item.ioc_created_at || "N/A",
            });
          });
        }
      }
    }
    return arr;
  }, [results]);


  // Apply filtering by keyword and source (case-insensitive substring match)
  const filteredResults = useMemo(() => {
    return flatResults.filter(
      (item) =>
        item.keyword.toLowerCase().includes(filterKeyword.toLowerCase()) &&
        item.source.toLowerCase().includes(filterSource.toLowerCase())
    );
  }, [flatResults, filterKeyword, filterSource]);

  // Sorting function
  const sortedResults = useMemo(() => {
    const sorted = [...filteredResults];
    const { column, direction } = sortConfig;

    sorted.sort((a, b) => {
      let valA = a[column];
      let valB = b[column];
      if (typeof valA === "string") valA = valA.toLowerCase();
      if (typeof valB === "string") valB = valB.toLowerCase();

      if (valA < valB) return direction === "asc" ? -1 : 1;
      if (valA > valB) return direction === "asc" ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [filteredResults, sortConfig]);

  // Pagination logic
  const totalPages = Math.max(1, Math.ceil(sortedResults.length / resultsPerPage));
  const paginatedResults = sortedResults.slice(
    (currentPage - 1) * resultsPerPage,
    currentPage * resultsPerPage
  );

  // Sorting toggle on header click
  const handleSort = (column) => {
    setSortConfig((current) => {
      if (current.column === column) {
        // toggle direction
        return {
          column,
          direction: current.direction === "asc" ? "desc" : "asc",
        };
      }
      return { column, direction: "asc" };
    });
  };

  // Handle page changes safely
  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;
    setCurrentPage(page);
  };

  const renderSortArrow = (column) => {
    if (sortConfig.column !== column) return null;
    return sortConfig.direction === "asc" ? " ▲" : " ▼";
  };

return (
  <SearchContainer>
    <div className="bg-gray-900 text-white py-6 px-4 min-h-screen max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-center">Scout</h2>
      <p className="text-lg text-gray-300 text-center max-w-3xl mx-auto mb-10">
        Scout searches open-source threat feeds to find phishing domains and spoofed infrastructure
        targeting your brand. Just enter a few keywords to get started.
      </p>

      {/* Keyword input */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <input
          type="text"
          placeholder="Add keyword (up to 5)"
          value={newKeyword}
          onChange={(e) => setNewKeyword(e.target.value)}
          className="flex-grow px-4 py-3 rounded-md bg-[#1f1f2e] border border-[#3c3c52] placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              addKeyword();
            }
          }}
        />
        <button
          onClick={addKeyword}
          className="px-6 py-3 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold transition-colors"
        >
          Add
        </button>
      </div>

      {error && <p className="text-red-500 mb-4 text-center font-medium">{error}</p>}

      {keywords.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-8 justify-center">
          {keywords.map((kw) => (
            <div
              key={kw}
              className="bg-[#3c3c52] px-4 py-2 rounded-full flex items-center gap-3"
            >
              <span>{kw}</span>
              <button
                onClick={() => removeKeyword(kw)}
                aria-label={`Remove keyword ${kw}`}
                className="text-red-400 hover:text-red-600 font-bold"
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Search button */}
      <div className="flex justify-center mb-8">
        <button
          onClick={searchKeywords}
          disabled={loading}
          className="px-8 py-3 bg-[#9b84e2] hover:bg-[#b3a3f1] rounded-md font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? "Searching..." : "Search Keywords"}
        </button>
      </div>

      {loading && (
        <div className="flex justify-center mb-8">
          <div className="w-6 h-6 border-4 border-[#9b84e2] border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {searchError && (
        <p className="text-red-500 mb-6 text-center font-medium">{searchError}</p>
      )}

      <div className="flex items-center justify-center mb-6">
        <label className="flex items-center gap-2 text-sm text-gray-300">
          <input
            type="checkbox"
            checked={enableFuzzing}
            onChange={(e) => setEnableFuzzing(e.target.checked)}
            className="accent-[#9b84e2] w-4 h-4"
          />
          Enable fuzzing (This will take longer)
        </label>
      </div>

      {/* Filters and controls (only show if results exist) */}
      {!loading && flatResults.length > 0 && (
        <div className="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {/* Filter inputs */}
          <div className="flex gap-3 flex-wrap justify-center sm:justify-start">
            <input
              type="text"
              placeholder="Filter by keyword"
              value={filterKeyword}
              onChange={(e) => {
                setFilterKeyword(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 rounded-md bg-[#1f1f2e] border border-[#3c3c52] placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
            />
            <input
              type="text"
              placeholder="Filter by source"
              value={filterSource}
              onChange={(e) => {
                setFilterSource(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 py-2 rounded-md bg-[#1f1f2e] border border-[#3c3c52] placeholder-gray-400 text-white focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
            />
          </div>

          {/* Export button and Results per page */}
          <div className="flex items-center gap-3 justify-center sm:justify-end">
            <button
              onClick={handleExport}
              className="px-4 py-2 bg-[#39e991] hover:bg-[#2ebd7f] rounded-md font-semibold transition-colors"
              aria-label="Export results"
            >
              Export
            </button>

            <label htmlFor="resultsPerPage" className="text-gray-300">
              Results per page:
            </label>
            <select
              id="resultsPerPage"
              value={resultsPerPage}
              onChange={(e) => {
                setResultsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="px-2 py-1 rounded-md bg-[#1f1f2e] border border-[#3c3c52] text-white focus:outline-none focus:ring-2 focus:ring-[#9b84e2]"
            >
              {RESULTS_PER_PAGE_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {opt}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {/* Results Table */}
      {!loading && flatResults.length > 0 && (
        <>
          <div className="overflow-x-auto mt-2">
            <table className="min-w-full table-auto border border-gray-700 text-left">
              <thead className="bg-[#2e2e40] cursor-pointer select-none">
                <tr className="text-left text-purple-300">
                  <th
                    className="px-4 py-2 border-b border-gray-600"
                    onClick={() => handleSort("keyword")}
                    aria-sort={sortConfig.column === "keyword" ? sortConfig.direction : "none"}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSort("keyword")}
                  >
                    Matched Keyword{renderSortArrow("keyword")}
                  </th>
                  <th
                    className="px-4 py-2 border-b border-gray-600"
                    onClick={() => handleSort("indicator")}
                    aria-sort={sortConfig.column === "indicator" ? sortConfig.direction : "none"}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSort("indicator")}
                  >
                    Indicator{renderSortArrow("indicator")}
                  </th>
                  <th
                    className="px-4 py-2 border-b border-gray-600"
                    onClick={() => handleSort("source")}
                    aria-sort={sortConfig.column === "source" ? sortConfig.direction : "none"}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSort("source")}
                  >
                    Source{renderSortArrow("source")}
                  </th>
                  <th
                    className="px-4 py-2 border-b border-gray-600"
                    onClick={() => handleSort("ioc_created_at")}
                    aria-sort={sortConfig.column === "ioc_created_at" ? sortConfig.direction : "none"}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && handleSort("ioc_created_at")}
                  >
                    First Seen{renderSortArrow("ioc_created_at")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedResults.map((item, idx) => (
                  <tr key={idx} className="hover:bg-[#3c3c52]">
                    <td className="px-4 py-2 border-b border-gray-700">{item.keyword}</td>
                    <td className="px-4 py-2 border-b border-gray-700">{item.indicator}</td>
                    <td className="px-4 py-2 border-b border-gray-700">{item.source}</td>
                    <td className="px-4 py-2 border-b border-gray-700">
                      {item.ioc_created_at ? new Date(item.ioc_created_at).toLocaleString() : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* No results message */}
      {!loading && flatResults.length === 0 && (
        <p className="text-gray-400 text-center">No results found.</p>
      )}

      {ransomwareResults.length > 0 && (
  <div className="mt-12">
    <h3 className="text-xl font-semibold mb-4 text-center text-[#39e991]">
      Ransomware Victim Matches
    </h3>
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto border border-gray-700 text-left">
        <thead className="bg-[#2e2e40]">
          <tr>
            <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Matched Keyword</th>
            <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Organization</th>
            <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Threat Actor</th>
            <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Date</th>
            <th className="py-2 px-3 border-b border-gray-700 max-w-xs">Link</th>
          </tr>
        </thead>
        <tbody>
          {ransomwareResults.map((entry, idx) => (
            <tr key={idx} className="hover:bg-[#3c3c52]">
              <td className="px-4 py-2 border-b border-gray-700">{entry.keyword}</td>
              <td className="px-4 py-2 border-b border-gray-700">{entry.organization}</td>
              <td className="px-4 py-2 border-b border-gray-700">{entry.threat_actor}</td>
              <td className="px-4 py-2 border-b border-gray-700">
                {entry.date_posted ? new Date(entry.date_posted).toLocaleDateString() : "N/A"}
              </td>
              <td className="px-4 py-2 border-b border-gray-700">
                {entry.source_url ? (
                  <a
                    href={entry.source_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[#39e991] underline hover:text-[#2ebd7f]"
                  >
                    View
                  </a>
                ) : (
                  "N/A"
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
)}

      {/* Pagination Controls */}
      {!loading && flatResults.length > 0 && (
        <div className="flex justify-center items-center gap-4 mt-4">
          <button
            onClick={() => goToPage(currentPage - 1)}
            disabled={currentPage === 1}
            className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Prev
          </button>
          <span>
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => goToPage(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="px-4 py-2 bg-[#6b5ca5] hover:bg-[#8473c9] rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}
    </div>
  </SearchContainer>
);

};

export default Scout;
